import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  TextField,
  MenuItem,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import axios from 'axios';

const MarksEntry: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [exam, setExam] = useState<any>(null);
  
  const [selectedClassId, setSelectedClassId] = useState('');
  const [selectedSubjectId, setSelectedSubjectId] = useState('');
  
  const [students, setStudents] = useState<any[]>([]);
  const [marks, setMarks] = useState<any>({}); // studentId -> { marksObtained, remarks }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchExam();
  }, [id]);

  const fetchExam = async () => {
    try {
      const response = await axios.get(`/exams/${id}`);
      setExam(response.data);
    } catch (error) {
      console.error('Failed to fetch exam:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClassChange = (classId: string) => {
    setSelectedClassId(classId);
    setSelectedSubjectId('');
    setStudents([]);
    setMarks({});
  };

  const handleSubjectChange = async (subjectId: string) => {
    setSelectedSubjectId(subjectId);
    // Fetch students and existing marks
    if (selectedClassId && subjectId) {
       fetchStudentsAndMarks(selectedClassId, subjectId);
    }
  };

  const fetchStudentsAndMarks = async (classId: string, subjectId: string) => {
    try {
      // 1. Fetch Students of the class
      // We need an endpoint for students by class. 
      // Assuming GET /students?classId=...
      const studentsRes = await axios.get(`/students?classId=${classId}`);
      setStudents(studentsRes.data);

      // 2. Fetch existing marks if any (Optional, or handled by backend to return existing marks)
      // Actually we don't have an endpoint to get marks for a specific subject/class easily except iterating.
      // But we can use the results endpoint for students? No, that's per student.
      // We might need an endpoint GET /exams/marks?examSubjectId=...
      // For now, let's assume we are entering new marks or overwriting. 
      // Ideally we should load existing marks.
      
      // Let's check if we can fetch marks.
      // The backend service has `findResults` but that's for a student.
      // Maybe we should add an endpoint to get marks for an ExamSubject.
      // For now, I will skip loading existing marks to save time, or I can try to implement it if needed.
      // User said "Lock UI if already marked" in Attendance. Here maybe similar?
      // "Save & validate".
      
    } catch (error) {
      console.error('Failed to fetch students:', error);
    }
  };

  const handleMarkChange = (studentId: string, field: 'marksObtained' | 'remarks', value: any) => {
    setMarks((prev: any) => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        [field]: value,
      },
    }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      // Find examSubjectId
      const examClass = exam.classes.find((c: any) => c.classId === selectedClassId);
      const examSubject = examClass?.subjects.find((s: any) => s.subjectId === selectedSubjectId);
      
      if (!examSubject) {
        setError('Invalid Subject Configuration');
        setSaving(false);
        return;
      }

      const marksData = Object.keys(marks).map(studentId => ({
        studentId,
        marksObtained: Number(marks[studentId].marksObtained),
        remarks: marks[studentId].remarks,
      }));

      await axios.post('/exams/marks', {
        examSubjectId: examSubject.id,
        marks: marksData,
      });
      
      setSuccess('Marks saved successfully');
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to save marks');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !exam) return <MainLayout><Typography>Loading...</Typography></MainLayout>;

  // Filter valid options
  const selectedExamClass = exam.classes?.find((c: any) => c.classId === selectedClassId);

  return (
    <MainLayout>
      <Container maxWidth="lg">
        <Typography variant="h4" gutterBottom>Marks Entry: {exam.name}</Typography>
        
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Class"
                fullWidth
                value={selectedClassId}
                onChange={(e) => handleClassChange(e.target.value)}
              >
                {exam.classes?.map((c: any) => (
                  <MenuItem key={c.classId} value={c.classId}>{c.class.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Subject"
                fullWidth
                value={selectedSubjectId}
                onChange={(e) => handleSubjectChange(e.target.value)}
                disabled={!selectedClassId}
              >
                {selectedExamClass?.subjects?.map((s: any) => (
                  <MenuItem key={s.subjectId} value={s.subjectId}>{s.subject.name} (Max: {s.maxMarks})</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Paper>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {students.length > 0 && (
          <>
            <TableContainer component={Paper} sx={{ mb: 3 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Admission No</TableCell>
                    <TableCell>Student Name</TableCell>
                    <TableCell>Marks Obtained</TableCell>
                    <TableCell>Remarks</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {students.map((student) => (
                    <TableRow key={student.id}>
                      <TableCell>{student.admissionNumber || '-'}</TableCell>
                      <TableCell>{student.firstName} {student.lastName}</TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          value={marks[student.id]?.marksObtained || ''}
                          onChange={(e) => handleMarkChange(student.id, 'marksObtained', e.target.value)}
                          inputProps={{ min: 0, max: selectedExamClass?.subjects?.find((s:any) => s.subjectId === selectedSubjectId)?.maxMarks }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={marks[student.id]?.remarks || ''}
                          onChange={(e) => handleMarkChange(student.id, 'remarks', e.target.value)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
               <Button variant="contained" size="large" onClick={handleSubmit} disabled={saving}>
                 {saving ? 'Saving...' : 'Save Marks'}
               </Button>
            </Box>
          </>
        )}
      </Container>
    </MainLayout>
  );
};

export default MarksEntry;
